import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import PipelineCard from "./PipelineCard";

const stages = [
  { id: "lead", label: "Lead", color: "bg-slate-100 border-slate-200" },
  { id: "prospect", label: "Prospect", color: "bg-blue-50 border-blue-200" },
  { id: "active", label: "Active", color: "bg-emerald-50 border-emerald-200" },
  { id: "inactive", label: "Inactive", color: "bg-amber-50 border-amber-200" },
  { id: "churned", label: "Churned", color: "bg-rose-50 border-rose-200" },
];

export default function PipelineView({ customers, onStatusChange, onCustomerClick }) {
  const handleDragEnd = (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;

    const customer = customers.find(c => c.id === draggableId);
    if (customer) {
      onStatusChange(customer, destination.droppableId);
    }
  };

  const getCustomersByStage = (stageId) => {
    return customers.filter(c => c.status === stageId);
  };

  const getTotalValue = (stageId) => {
    return getCustomersByStage(stageId).reduce((sum, c) => sum + (c.deal_value || 0), 0);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="flex gap-4 overflow-x-auto pb-4">
        {stages.map((stage) => {
          const stageCustomers = getCustomersByStage(stage.id);
          const totalValue = getTotalValue(stage.id);
          
          return (
            <div key={stage.id} className="flex-shrink-0 w-80">
              <Card className={cn("border-2", stage.color)}>
                <div className="p-4 border-b border-inherit">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-slate-900">{stage.label}</h3>
                    <span className="px-2 py-0.5 rounded-full bg-white text-xs font-medium text-slate-600">
                      {stageCustomers.length}
                    </span>
                  </div>
                  {totalValue > 0 && (
                    <p className="text-xs text-slate-500">
                      {totalValue.toLocaleString()} kr total
                    </p>
                  )}
                </div>
                
                <Droppable droppableId={stage.id}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={cn(
                        "p-3 min-h-[500px] transition-colors",
                        snapshot.isDraggingOver && "bg-slate-50"
                      )}
                    >
                      {stageCustomers.map((customer, index) => (
                        <Draggable
                          key={customer.id}
                          draggableId={customer.id}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={cn(
                                "transition-shadow",
                                snapshot.isDragging && "shadow-xl rotate-2"
                              )}
                            >
                              <PipelineCard
                                customer={customer}
                                onClick={(e) => {
                                  if (!snapshot.isDragging) {
                                    onCustomerClick(customer);
                                  }
                                }}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      
                      {stageCustomers.length === 0 && (
                        <div className="text-center py-8 text-slate-400 text-sm">
                          No customers in this stage
                        </div>
                      )}
                    </div>
                  )}
                </Droppable>
              </Card>
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
}